import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    CompanyName: state.components.contactUsForm.fieldValues["CompanyName"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CompanyNameStyles {
    companyNameTextField?: TextFieldPresentationProps;
}

export const CompanyNameStyles: CompanyNameStyles = {
    companyNameTextField: {
        css: css`
            border-radius: 8px !important;
        `,
        cssOverrides: {
            formField: css`
                margin-bottom: 20px;
            `,

            formInputWrapper: css`
                margin-top: 4px !important;
            `,
        },
    },
};

const styles = CompanyNameStyles;

const CompanyName: React.FC<Props> = ({ fields, CompanyName, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [CompanyNameErrorMessage, setCompanyNameErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.CompanyName = validateCompanyName;
        if (typeof CompanyName !== "undefined") {
            validateCompanyName();
        }
    }, [CompanyName]);

    React.useEffect(() => {
        contactFormContext.validators.CompanyName = validateCompanyName;
    }, [isRequired]);

    const validateCompanyName = () => {
        const errorMessage = isRequired && !CompanyName ? (siteMessage("Field_Required", label) as string) : "";
        setCompanyNameErrorMessage(errorMessage);
        return !errorMessage;
    };

    const CompanyNameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "CompanyName", value: event.target.value });
    };

    return (
        <TextField
            {...styles.companyNameTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={CompanyName || ""}
            error={CompanyNameErrorMessage}
            onChange={CompanyNameChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CompanyName),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        displayName: "CompanyName",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
